import React, { useState, useRef } from 'react'
import Editor, { OnMount } from '@monaco-editor/react'
import { Input, Select, Modal, Tooltip } from 'antd'
import {
  useCreateFunction,
  mutateFunction,
} from '../../CreateFunctionContext'
import { a8AstroFunctions, a8flowFunctions } from '../../constants'
import { IFunction } from '../../types'
import './styles/functions.scss'

// icons
import { ReactComponent as PlusIcon } from '../../../../../../assets/svg/MarketplaceFunctions/addBtn.svg'
import { ReactComponent as MoreIcon } from '../../../../../../assets/svg/more.svg'
import { ReactComponent as DeleteIcon } from '../../../../../../assets/svg/delete.svg'
import { ReactComponent as EditIcon } from '../../../../../../assets/svg/editIcon.svg'
import { ReactComponent as CloseIcon } from '../../../../../../assets/svg/close_btn_rounded.svg'

const { Option } = Select

const defaultState = {
  sdk: 'a8flow',
  name: a8flowFunctions[0],
  resp: '',
  type: 'string',
}

const AddFunction = () => {
  const { dispatch } = useCreateFunction()
  const [modalVisibility, setModalVisibility] = useState(false)
  const editorRef = useRef<any>(null)
  const [fn, setFn] = useState<IFunction>(defaultState)

  const updateFn = (key: string, val: string) =>
    setFn((prevState) => ({ ...prevState, [key]: val }))

  return (
    <div className="add-fn">
      <div className="add-fn-dropdown">
        <div style={{ flex: 1 }}>
          <p style={{ marginBottom: 6 }}>SDKs</p>
          <Select
            style={{ width: '100%' }}
            value={fn.sdk}
            onChange={(sdk: string) => {
              if (sdk === 'a8func') {
                updateFn('name', '')
              } else if(sdk === 'a8func') {
                updateFn('name', a8flowFunctions[0])
              } else{
                updateFn('name', a8AstroFunctions[0])
              }
              updateFn('sdk', sdk)
            }}
          >
            <Option value="a8flow">A8Flow</Option>
            <Option value="a8func">A8Function</Option>
            <Option value="a8Astro">A8Astro</Option>
          </Select>
        </div>

        {fn.sdk === 'a8flow' && (
          <div style={{ flex: 1 }}>
            <p style={{ marginBottom: 6 }}>Functions</p>
            <Select
              onChange={(name: string) => updateFn('name', name)}
              value={fn.name}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              dropdownMenuStyle={{ width: 200 }}
            >
              {a8flowFunctions.map((func) => (
                <Option key={func} value={func} title={func}>
                  {func}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {fn.sdk === 'a8Astro' && (
          <div style={{ flex: 1 }}>
            <p style={{ marginBottom: 6 }}>Functions</p>
            <Select
              onChange={(name: string) => updateFn('name', name)}
              value={fn.name}
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              dropdownMenuStyle={{ width: 200 }}
            >
              {a8AstroFunctions.map((func) => (
                <Option key={func} value={func} title={func}>
                  {func}
                </Option>
              ))}
            </Select>
          </div>
        )}

        <div className="add-fn-add">
          <PlusIcon
            width={28}
            height={28}
            onClick={() => {
              dispatch(mutateFunction({ type: 'add', data: fn }))
              setFn(defaultState)
              if (editorRef.current !== null) {
                editorRef.current.setValue('')
              }
            }}
          />
        </div>
      </div>

      {fn.sdk === 'a8func' && (
        <div className="add-fn-a8func">
          <p>Function</p>
          <Input
            placeholder="Enter function name"
            value={fn.name}
            onChange={(e) => {
              const value = e.target.value
              updateFn('name', value)
            }}
          />
        </div>
      )}

      <div className="add-fn-resp" onClick={() => setModalVisibility(true)}>
        <p>
          {fn.resp ? fn.resp : 'click here to write your function response'}
        </p>
      </div>

      <FunctionEditor
        updateFn={updateFn}
        editorRef={editorRef}
        fn={fn}
        modalVisibility={modalVisibility}
        setModalVisibility={setModalVisibility}
      />
    </div>
  )
}

export const Functions = () => {
  const { state } = useCreateFunction()
  const functions = state.context.functions

  return (
    <div className="fns-container">
      <AddFunction />
      <hr />
      <div className="fns">
        {functions.map((fn, idx) => (
          <Func key={idx} fn={fn} idx={idx} />
        ))}
      </div>
    </div>
  )
}

interface IFunc {
 fn: IFunction 
 idx: number 
}

const Func: React.FC<IFunc> = (props) => {
  const { fn, idx } = props
  const [tooltipVisibility, setTooltipVisibility] = useState(false)
  const { dispatch } = useCreateFunction()
  const [modalVisibility, setModalVisibility] = useState(false)
  const editorRef = useRef<any>(null)

  return (
    <div className="fn" key={fn.name}>
      <Highlight title="SDK" value={fn.sdk} />
      <Highlight title="Function" value={fn.name} />
      <div className="add-fn-resp">
        <p>{fn.resp}</p>
      </div>

      <Tooltip
        placement="topLeft"
        trigger="click"
        overlayClassName="widgetCardTooltip"
        visible={tooltipVisibility}
        onVisibleChange={setTooltipVisibility}
        title={
          <FunctionTooltip
            fn={fn}
            idx={idx}
            setTooltipVisibility={setTooltipVisibility}
            setModalVisibility={setModalVisibility}
          />
        }
      >
        <MoreIcon className="fn-options" height="16px" />
      </Tooltip>

      <FunctionEditor
        editorRef={editorRef}
        modalVisibility={modalVisibility}
        setModalVisibility={setModalVisibility}
        fn={fn}
        updateFn={(key: string, value: string) => {
          dispatch(
            mutateFunction({
              data: {
                ...fn,
                [key]: value,
              },
              idx,
              type: 'update',
            })
          )
        }}
      />
    </div>
  )
}

interface IHighlight {
  title: string
  value: string
}

const Highlight = (props: IHighlight) => {
  const { title, value } = props
  return (
    <p className="fn-highlight">
      <span>{title}: </span>
      <span>{value}</span>
    </p>
  )
}

interface IFunctionTooltip {
  setTooltipVisibility: (visibility: boolean) => void
  setModalVisibility: (visibility: boolean) => void
  idx: number
  fn: IFunction
}

const FunctionTooltip: React.FC<IFunctionTooltip> = (props) => {
  const { dispatch } = useCreateFunction()
  const { idx, setTooltipVisibility, setModalVisibility } = props

  return (
    <div className="fn-tooltip">
      <div
        onClick={() => {
          setModalVisibility(true)
          setTooltipVisibility(false)
        }}
      >
        <EditIcon height={16} width={16} />
        <p style={{ color: "#292d32" }} >Edit</p>
      </div>
      <div
        onClick={() => {
          dispatch(
            mutateFunction({
              idx,
              type: 'delete',
            })
          )
          setTooltipVisibility(false)
        }}
      >
        <DeleteIcon height={16} width={16} />
        <p style={{ color: "#292d32" }} >Delete</p>
      </div>
    </div>
  )
}

interface IFunctionEditor {
  editorRef: any
  modalVisibility: boolean
  setModalVisibility: (visibility: boolean) => void
  fn: IFunction
  updateFn: (key: string, val: string) => void
}

const FunctionEditor: React.FC<IFunctionEditor> = (props) => {
  const { editorRef, modalVisibility, setModalVisibility, fn, updateFn } = props

  const handleEditorDidMount: OnMount = (editor) => {
    editorRef.current = editor
  }

  return (
    <Modal
      destroyOnClose
      className="add-fn-modal"
      title={
        <ModalHeader
          updateFn={updateFn}
          fn={fn}
          close={() => setModalVisibility(false)}
        />
      }
      visible={modalVisibility}
      footer={
        <div>
          <button
            onClick={() => {
              setModalVisibility(false)
              if (editorRef.current != null) {
                const value = editorRef.current.getValue()
                updateFn('resp', value)
              }
            }}
          >
            save
          </button>
        </div>
      }
      width={800}
      onCancel={() => setModalVisibility(false)}
    >
      <Editor
        onMount={handleEditorDidMount}
        theme="light"
        defaultValue={fn.resp}
        language={fn.type === 'json' ? 'json' : 'text'}
        height={600}
        options={{
          minimap: {
            enabled: false,
          },
          wordWrap: 'on',
          fontSize: 16,
          quickSuggestions: false,
          overviewRulerLanes: 0,
          scrollbar: {
            horizontal: 'hidden',
          },
          overviewRulerBorder: false,
          renderLineHighlight: 'none',
        }}
      />
    </Modal>
  )
}

interface IModalHeader {
   close: () => void
   updateFn: (key: string, value: string) => void
   fn: IFunction
}

const ModalHeader: React.FC<IModalHeader> = (props) => {
  const { close, updateFn, fn } = props

  return (
    <div className="add-fn-modal-header">
      <div>
        <p>Code Editor</p>
        <Select
          defaultValue={fn.type}
          style={{ width: 140 }}
          onChange={(type: string) => updateFn('type', type)}
        >
          <Option key="string">String</Option>
          <Option key="number">Number</Option>
          <Option key="boolean">Boolean</Option>
          <Option key="json">JSON</Option>
        </Select>
      </div>
      <CloseIcon onClick={close} />
    </div>
  )
}
